import { ComponentProps, useEffect, useState } from "react";

import CheckCircleIcon from "@heroicons/react/20/solid/CheckCircleIcon";
import _get from "lodash/get";

import { FormDocument, ProductDocumentDataDrawersItem } from "prismicio-types";
import { useGetCustomisation } from "queries/product-queries";
import { useInterpolate } from "utils/prismic-utils";
import { useFindByKey } from "utils/schema-utils";

import type Drawers from "../Drawers";

import styles from "./Drawer.module.scss";

type DrawerProps = {
  data: ProductDocumentDataDrawersItem;
  onDrawerClicked: ComponentProps<typeof Drawers>["onDrawerClicked"];
};

export default function Drawer({ data, onDrawerClicked }: DrawerProps) {
  const findByKey = useFindByKey();
  const { data: customisation } = useGetCustomisation();
  const interpolate = useInterpolate();

  const [isComplete, setIsComplete] = useState(false);

  const form = (data as any).form as FormDocument;

  if (
    !data.label ||
    !data.active_value ||
    !data.placeholder_value ||
    !data.muse_schema_key ||
    !form.data
  ) {
    // Tell the user which of the essential fields are missing
    const missingFields = Object.entries({
      label: data.label,
      active_value: data.active_value,
      placeholder_value: data.placeholder_value,
      muse_schema_key: data.muse_schema_key,
      form: form.data,
    }).reduce(
      (list, [key, value]) => (!value ? [...list, key] : list),
      [] as string[]
    );

    throw new Error(
      `Invalid configuration for drawer "${
        data.label
      }" - missing "${missingFields.join('", "')}"`
    );
  }

  // The name of this function is the same of a proprietaty testing library
  // function so we we need to disable the eslint rule
  // eslint-disable-next-line testing-library/await-async-query
  const field = findByKey(data.muse_schema_key);

  useEffect(() => {
    if (data.muse_schema_key && field) {
      setIsComplete(_get(customisation, field.urlKey, false) !== false);
    }
  }, [customisation, data.muse_schema_key, field]);

  return (
    <button
      aria-label={interpolate(data.label)}
      className={[styles.drawer, isComplete && styles.complete].join(" ")}
      onClick={() => onDrawerClicked(form)}
    >
      <>
        <span className={styles.label}>{interpolate(data.label)}</span>
        <p className={styles.valueContainer}>
          <CheckCircleIcon
            className={[styles.tick, isComplete && styles.complete].join(" ")}
          />

          {isComplete && (
            <span className={styles.value}>
              {interpolate(data.active_value)}
            </span>
          )}

          {!isComplete && (
            <span className={styles.placeholder}>
              {interpolate(data.placeholder_value)}
            </span>
          )}
        </p>
      </>
    </button>
  );
}
